export interface CombatTip {
  icon: string;
  title: string;
  description: string;
  priority: number; // Higher = shown first
}

export interface TipContext {
  classNames: string[];
  featureIds: string[];
  hasSpellcasting: boolean;
  level: number;
  raceName?: string;
  subraceName?: string;
}

// Class-specific combat tips
const CLASS_TIPS: Record<string, CombatTip[]> = {
  barbarian: [
    {
      icon: '🔥',
      title: 'Rage First',
      description: 'Rage (bonus action) grants resistance to physical damage and +2 melee damage. Use it on turn 1.',
      priority: 10,
    },
    {
      icon: '⚔️',
      title: 'Reckless Attack',
      description: 'Attack with advantage, but enemies get advantage on you until your next turn. High risk, high reward.',
      priority: 8,
    },
    {
      icon: '💪',
      title: 'Unarmored Defense',
      description: 'Your AC = 10 + DEX + CON. No armor needed if your stats are high.',
      priority: 5,
    },
  ],
  bard: [
    {
      icon: '🎵',
      title: 'Bardic Inspiration',
      description: 'Give an ally a d6 (bonus action) to add to an attack, save, or check. Use it before short rests.',
      priority: 10,
    },
    {
      icon: '✨',
      title: 'Versatile Caster',
      description: 'Bards can learn spells from any class. Prioritize support and control spells.',
      priority: 6,
    },
  ],
  cleric: [
    {
      icon: '🛡️',
      title: 'Spiritual Weapon',
      description: 'Cast once, then attack as a bonus action each turn. No concentration required.',
      priority: 10,
    },
    {
      icon: '💫',
      title: 'Channel Divinity',
      description: "Your domain's Channel Divinity recharges on short rest. Don't forget to use it.",
      priority: 8,
    },
    {
      icon: '❤️',
      title: 'Healing Word > Cure Wounds',
      description: 'Healing Word is a bonus action at range. Save it for downed allies.',
      priority: 7,
    },
  ],
  druid: [
    {
      icon: '🐺',
      title: 'Wild Shape',
      description: 'Wild Shape HP is separate. When beast form drops to 0, you revert with your original HP.',
      priority: 10,
    },
    {
      icon: '🌿',
      title: 'Concentration Control',
      description: 'Entangle, Moonbeam, Call Lightning - your best spells need concentration. Protect it.',
      priority: 8,
    },
  ],
  fighter: [
    {
      icon: '💨',
      title: 'Second Wind',
      description: 'Heal 1d10 + fighter level as a bonus action. Recharges on short rest.',
      priority: 10,
    },
    {
      icon: '⚡',
      title: 'Action Surge',
      description: 'Take a second Action this turn. Best used with Extra Attack for burst damage.',
      priority: 9,
    },
    {
      icon: '🎯',
      title: 'Fighting Style',
      description: "Your fighting style defines your role. Lean into it - use weapons that match.",
      priority: 5,
    },
  ],
  monk: [
    {
      icon: '👊',
      title: 'Flurry of Blows',
      description: '1 ki point for 2 unarmed strikes as a bonus action. Great for burst damage.',
      priority: 10,
    },
    {
      icon: '🏃',
      title: 'Step of the Wind',
      description: '1 ki to Dash or Disengage as bonus action + double jump distance.',
      priority: 8,
    },
    {
      icon: '🛡️',
      title: 'Patient Defense',
      description: '1 ki to Dodge as bonus action. Use when surrounded or low on HP.',
      priority: 7,
    },
    {
      icon: '🖐️',
      title: 'Stunning Strike',
      description: 'On hit, spend 1 ki to force CON save or target is stunned. Devastating if it lands.',
      priority: 9,
    },
  ],
  paladin: [
    {
      icon: '⚔️',
      title: 'Divine Smite',
      description: 'Declare after you hit. 2d8 radiant + 1d8 per slot level above 1st. Crit = double dice!',
      priority: 10,
    },
    {
      icon: '🙏',
      title: 'Lay on Hands',
      description: '5 HP per paladin level pool. Can use any amount per action. Great for picking up downed allies.',
      priority: 8,
    },
    {
      icon: '💫',
      title: 'Aura of Protection',
      description: 'At level 6+, you and allies within 10ft add your CHA to all saves.',
      priority: 7,
    },
  ],
  ranger: [
    {
      icon: '🎯',
      title: "Hunter's Mark",
      description: "1d6 extra damage per hit. Move it to new target when current dies (bonus action).",
      priority: 10,
    },
    {
      icon: '🌲',
      title: 'Favored Enemy',
      description: 'Advantage on Survival to track and INT checks to recall info about your favored enemies.',
      priority: 5,
    },
  ],
  rogue: [
    {
      icon: '🗡️',
      title: 'Sneak Attack',
      description: 'Triggers with advantage OR ally within 5ft of target. No hiding required!',
      priority: 10,
    },
    {
      icon: '🏃',
      title: 'Cunning Action',
      description: 'Dash, Disengage, or Hide as a bonus action every turn. Mobility is your strength.',
      priority: 9,
    },
    {
      icon: '🛡️',
      title: 'Uncanny Dodge',
      description: 'Reaction when hit to halve damage. Use against big hits, save for attacks you can see.',
      priority: 8,
    },
  ],
  sorcerer: [
    {
      icon: '✨',
      title: 'Metamagic',
      description: 'Quickened Spell casts a spell as bonus action. Twinned doubles single-target spells.',
      priority: 10,
    },
    {
      icon: '🔮',
      title: 'Font of Magic',
      description: 'Convert sorcery points to spell slots or vice versa. Flexibility is power.',
      priority: 8,
    },
    {
      icon: '💥',
      title: 'Careful Spell',
      description: 'Allies auto-succeed on your AOE saves. Fireball without friendly fire.',
      priority: 7,
    },
  ],
  warlock: [
    {
      icon: '💀',
      title: 'Eldritch Blast',
      description: 'Your bread and butter. Multiple beams = multiple Agonizing Blast damage.',
      priority: 10,
    },
    {
      icon: '😴',
      title: 'Short Rest Recovery',
      description: 'All spell slots return on short rest. Use them freely, rest often.',
      priority: 9,
    },
    {
      icon: '🎁',
      title: 'Invocations',
      description: 'Agonizing Blast adds CHA to damage. Devil\'s Sight sees in magical darkness.',
      priority: 7,
    },
  ],
  wizard: [
    {
      icon: '📖',
      title: 'Spell Preparation',
      description: 'Prepare INT + wizard level spells daily. Adapt your loadout to the situation.',
      priority: 8,
    },
    {
      icon: '🔮',
      title: 'Arcane Recovery',
      description: 'Recover spell slots on short rest (half wizard level, rounded up). Use it!',
      priority: 9,
    },
    {
      icon: '🎯',
      title: 'Control the Battlefield',
      description: 'Web, Hypnotic Pattern, Wall spells - control wins fights. Damage is secondary.',
      priority: 10,
    },
  ],
};

// Feature-specific tips (override or add to class tips)
const FEATURE_TIPS: Record<string, CombatTip> = {
  'extra-attack': {
    icon: '⚔️',
    title: 'Extra Attack',
    description: 'Attack twice when you take the Attack action. Consider splitting targets.',
    priority: 7,
  },
  'evasion': {
    icon: '💨',
    title: 'Evasion',
    description: 'DEX saves for half damage become no damage on success, half on fail.',
    priority: 8,
  },
  'sneak-attack': {
    icon: '🗡️',
    title: 'Sneak Attack',
    description: 'Once per turn, add extra damage when you have advantage or an ally is adjacent to target.',
    priority: 10,
  },
};

// Generic tips for all characters
const GENERIC_TIPS: CombatTip[] = [
  {
    icon: '🛡️',
    title: 'Dodge Action',
    description: 'All attacks against you have disadvantage until your next turn. Great when surrounded.',
    priority: 3,
  },
  {
    icon: '🤝',
    title: 'Help Action',
    description: "Give an ally advantage on their next attack or check. Doesn't cost resources.",
    priority: 2,
  },
  {
    icon: '⏳',
    title: 'Ready Action',
    description: 'Set a trigger and reaction. "When the enemy moves, I attack." Powerful for timing.',
    priority: 2,
  },
];

// Spellcaster tips
const SPELLCASTER_TIPS: CombatTip[] = [
  {
    icon: '🎯',
    title: 'Concentration',
    description: 'When hit, CON save (DC 10 or half damage). Losing concentration ends your spell.',
    priority: 9,
  },
  {
    icon: '♾️',
    title: 'Cantrips Are Free',
    description: 'Save spell slots for key moments. Cantrips scale with level and cost nothing.',
    priority: 6,
  },
];

// Race-specific combat tips
const RACE_TIPS: Record<string, CombatTip[]> = {
  dragonborn: [
    {
      icon: '🐉',
      title: 'Breath Weapon',
      description: 'Action: 5×30ft line or 15ft cone. DEX/CON save, 2d6 damage. Recharges on short rest.',
      priority: 9,
    },
    {
      icon: '🔥',
      title: 'Damage Resistance',
      description: 'You resist your draconic ancestry damage type. Position yourself to absorb those attacks.',
      priority: 6,
    },
  ],
  dwarf: [
    {
      icon: '💪',
      title: 'Dwarven Resilience',
      description: 'Advantage on saves vs poison, resistance to poison damage. Engage poisonous enemies confidently.',
      priority: 7,
    },
  ],
  elf: [
    {
      icon: '✨',
      title: 'Fey Ancestry',
      description: "Advantage vs charm, immune to magical sleep. You're reliable against enchantment effects.",
      priority: 6,
    },
    {
      icon: '👁️',
      title: 'Trance',
      description: '4 hours of trance = 8 hours of sleep. Great for longer watch shifts.',
      priority: 3,
    },
  ],
  'half-elf': [
    {
      icon: '✨',
      title: 'Fey Ancestry',
      description: "Advantage vs charm, immune to magical sleep. You're reliable against enchantment effects.",
      priority: 6,
    },
  ],
  'half-orc': [
    {
      icon: '💀',
      title: 'Relentless Endurance',
      description: 'Drop to 1 HP instead of 0 (once per long rest). Fight aggressively - you have a safety net.',
      priority: 9,
    },
    {
      icon: '⚔️',
      title: 'Savage Attacks',
      description: 'Extra damage die on melee crits. Crit-fishing builds shine here.',
      priority: 7,
    },
  ],
  halfling: [
    {
      icon: '🍀',
      title: 'Lucky',
      description: 'Reroll natural 1s on attacks, saves, and checks. Your floor is higher than most.',
      priority: 8,
    },
    {
      icon: '🦁',
      title: 'Brave',
      description: 'Advantage on saves vs frightened. Stand firm against dragons and other terrors.',
      priority: 5,
    },
  ],
  gnome: [
    {
      icon: '🧠',
      title: 'Gnome Cunning',
      description: 'Advantage on INT/WIS/CHA saves vs magic. Excellent against spellcasters.',
      priority: 8,
    },
  ],
  tiefling: [
    {
      icon: '🔥',
      title: 'Hellish Rebuke',
      description: 'At level 3: Cast as reaction when damaged. 2d10 fire damage, no spell slot needed (1/day).',
      priority: 8,
    },
    {
      icon: '🔥',
      title: 'Fire Resistance',
      description: 'You resist fire damage. Position to absorb fire attacks for your party.',
      priority: 6,
    },
  ],
  human: [
    {
      icon: '🎯',
      title: 'Versatile',
      description: 'No special combat abilities, but your extra skills/feats make you adaptable.',
      priority: 3,
    },
  ],
  aasimar: [
    {
      icon: '👼',
      title: 'Healing Hands',
      description: 'Heal HP equal to your level as an action (1/long rest). Save for clutch moments.',
      priority: 7,
    },
    {
      icon: '✨',
      title: 'Celestial Resistance',
      description: 'Resist necrotic and radiant damage. Strong against undead and celestials.',
      priority: 6,
    },
  ],
  goliath: [
    {
      icon: '🪨',
      title: "Stone's Endurance",
      description: 'Reaction: Reduce damage by 1d12+CON (1/short rest). Save for big hits.',
      priority: 9,
    },
    {
      icon: '💪',
      title: 'Powerful Build',
      description: 'Count as Large for carrying. Grapple bigger creatures, carry more loot.',
      priority: 4,
    },
  ],
  tabaxi: [
    {
      icon: '🐱',
      title: 'Feline Agility',
      description: 'Double speed until end of turn (recharges when you move 0 ft). Burst mobility.',
      priority: 8,
    },
    {
      icon: '🐾',
      title: 'Cat Claws',
      description: '1d4+STR slashing unarmed strikes + 20ft climb speed. Never truly unarmed.',
      priority: 5,
    },
  ],
  tortle: [
    {
      icon: '🐢',
      title: 'Natural Armor',
      description: 'AC 17 (no DEX bonus). Dump DEX for other stats - you have built-in plate.',
      priority: 8,
    },
    {
      icon: '🛡️',
      title: 'Shell Defense',
      description: 'Action: +4 AC, advantage on STR/CON saves, but prone and speed 0. Emergency button.',
      priority: 6,
    },
  ],
  kenku: [
    {
      icon: '🦅',
      title: 'Expert Forgery',
      description: 'Advantage on checks to produce forgeries. Creative problem-solving tool.',
      priority: 3,
    },
  ],
  triton: [
    {
      icon: '🌊',
      title: 'Control Air and Water',
      description: 'Cast Fog Cloud, Gust of Wind, Wall of Water (1/long rest each). Battlefield control.',
      priority: 7,
    },
    {
      icon: '🐟',
      title: 'Amphibious',
      description: 'Breathe underwater, 30ft swim speed. Underwater combat is your domain.',
      priority: 5,
    },
  ],
  firbolg: [
    {
      icon: '👻',
      title: 'Hidden Step',
      description: 'Bonus action: Invisible until start of next turn (1/short rest). Free advantage setup.',
      priority: 9,
    },
    {
      icon: '🗣️',
      title: 'Speech of Beast and Leaf',
      description: 'Communicate with beasts and plants. Creative scouting and distraction options.',
      priority: 4,
    },
  ],
  bugbear: [
    {
      icon: '🎯',
      title: 'Surprise Attack',
      description: '+2d6 damage if target is surprised (first round only). Prioritize ambushes.',
      priority: 9,
    },
    {
      icon: '📏',
      title: 'Long-Limbed',
      description: '+5ft reach on melee attacks on your turn. Hit-and-run without opportunity attacks.',
      priority: 7,
    },
  ],
  goblin: [
    {
      icon: '🏃',
      title: 'Nimble Escape',
      description: 'Disengage or Hide as bonus action. Rogue-like mobility for any class.',
      priority: 9,
    },
    {
      icon: '💢',
      title: 'Fury of the Small',
      description: 'Extra damage = your level vs larger creatures (1/short rest). Nova damage.',
      priority: 8,
    },
  ],
  hobgoblin: [
    {
      icon: '🎖️',
      title: 'Saving Face',
      description: 'Add +1 to +5 to failed roll based on nearby allies. Clutch save recovery.',
      priority: 8,
    },
  ],
  kobold: [
    {
      icon: '📦',
      title: 'Pack Tactics',
      description: 'Advantage when ally is within 5ft of target. Always position near allies.',
      priority: 9,
    },
  ],
  lizardfolk: [
    {
      icon: '🦎',
      title: 'Hungry Jaws',
      description: 'Bonus action bite: 1d6+STR, gain temp HP on hit (1/short rest). Free sustain.',
      priority: 8,
    },
    {
      icon: '🛡️',
      title: 'Natural Armor',
      description: 'AC 13 + DEX unarmored. Decent baseline without equipment.',
      priority: 5,
    },
  ],
  orc: [
    {
      icon: '🏃',
      title: 'Aggressive',
      description: 'Bonus action: Move up to speed toward enemy. Close gaps without Dash.',
      priority: 8,
    },
  ],
  yuan_ti: [
    {
      icon: '🐍',
      title: 'Magic Resistance',
      description: 'Advantage on saves vs spells and magical effects. Incredible vs casters.',
      priority: 10,
    },
    {
      icon: '☠️',
      title: 'Poison Immunity',
      description: "Immune to poison damage and poisoned condition. Poison can't touch you.",
      priority: 7,
    },
  ],
};

// Subrace-specific tips (override or add to race tips)
const SUBRACE_TIPS: Record<string, CombatTip[]> = {
  'high-elf': [
    {
      icon: '✨',
      title: 'Free Cantrip',
      description: 'You know one wizard cantrip (INT-based). Extra ranged option or utility.',
      priority: 6,
    },
  ],
  'wood-elf': [
    {
      icon: '🏃',
      title: 'Fleet of Foot',
      description: '35ft base speed. Extra 5ft matters for hit-and-run tactics.',
      priority: 5,
    },
    {
      icon: '🌲',
      title: 'Mask of the Wild',
      description: 'Hide when lightly obscured by nature. Ambush opportunities in forests.',
      priority: 6,
    },
  ],
  drow: [
    {
      icon: '🌑',
      title: 'Superior Darkvision',
      description: '120ft darkvision. Extinguish lights to blind enemies while you see fine.',
      priority: 7,
    },
    {
      icon: '✨',
      title: 'Drow Magic',
      description: 'Dancing Lights, Faerie Fire (lvl 3), Darkness (lvl 5). Free spells 1/day.',
      priority: 8,
    },
  ],
  'mountain-dwarf': [
    {
      icon: '🛡️',
      title: 'Armor Training',
      description: 'Light and medium armor proficiency. Wizards in half-plate!',
      priority: 8,
    },
  ],
  'hill-dwarf': [
    {
      icon: '❤️',
      title: 'Dwarven Toughness',
      description: '+1 HP per level. Extra survivability adds up fast.',
      priority: 6,
    },
  ],
  lightfoot: [
    {
      icon: '👻',
      title: 'Naturally Stealthy',
      description: 'Hide behind Medium creatures. Use allies as mobile cover.',
      priority: 7,
    },
  ],
  stout: [
    {
      icon: '💪',
      title: 'Stout Resilience',
      description: 'Advantage vs poison, resistance to poison damage. Dwarven fortitude.',
      priority: 6,
    },
  ],
  'forest-gnome': [
    {
      icon: '🐿️',
      title: 'Speak with Small Beasts',
      description: 'Communicate with small animals. Creative scouting via squirrels.',
      priority: 4,
    },
  ],
  'rock-gnome': [
    {
      icon: '🔧',
      title: 'Tinker',
      description: 'Create clockwork devices. Distractions, alarms, and creative solutions.',
      priority: 4,
    },
  ],
  'protector-aasimar': [
    {
      icon: '👼',
      title: 'Radiant Soul',
      description: 'At level 3: Fly 30ft, +level radiant damage for 1 minute (1/long rest). Transformation!',
      priority: 9,
    },
  ],
  'scourge-aasimar': [
    {
      icon: '💥',
      title: 'Radiant Consumption',
      description: 'At level 3: Bright light, damage nearby enemies + bonus radiant (1/long rest). AoE nova.',
      priority: 9,
    },
  ],
  'fallen-aasimar': [
    {
      icon: '😱',
      title: 'Necrotic Shroud',
      description: 'At level 3: Frighten nearby enemies, +level necrotic damage (1/long rest). Fear aura.',
      priority: 9,
    },
  ],
};

/**
 * Generate combat tips based on character context
 */
export function generateCombatTips(context: TipContext): CombatTip[] {
  const tips: CombatTip[] = [];
  const seenTitles = new Set<string>();

  // Add class-specific tips
  for (const className of context.classNames) {
    const classKey = className.toLowerCase();
    const classTips = CLASS_TIPS[classKey];
    if (classTips) {
      for (const tip of classTips) {
        if (!seenTitles.has(tip.title)) {
          tips.push(tip);
          seenTitles.add(tip.title);
        }
      }
    }
  }

  // Add race-specific tips
  if (context.raceName) {
    const raceKey = context.raceName.toLowerCase().replace(/\s+/g, '-');
    const raceTips = RACE_TIPS[raceKey];
    if (raceTips) {
      for (const tip of raceTips) {
        if (!seenTitles.has(tip.title)) {
          tips.push(tip);
          seenTitles.add(tip.title);
        }
      }
    }
  }

  // Add subrace-specific tips
  if (context.subraceName) {
    const subraceKey = context.subraceName.toLowerCase().replace(/\s+/g, '-');
    const subraceTips = SUBRACE_TIPS[subraceKey];
    if (subraceTips) {
      for (const tip of subraceTips) {
        if (!seenTitles.has(tip.title)) {
          tips.push(tip);
          seenTitles.add(tip.title);
        }
      }
    }
  }

  // Add feature-specific tips (may override class tips)
  for (const featureId of context.featureIds) {
    const featureTip = FEATURE_TIPS[featureId];
    if (featureTip && !seenTitles.has(featureTip.title)) {
      tips.push(featureTip);
      seenTitles.add(featureTip.title);
    }
  }

  // Add spellcaster tips
  if (context.hasSpellcasting) {
    for (const tip of SPELLCASTER_TIPS) {
      if (!seenTitles.has(tip.title)) {
        tips.push(tip);
        seenTitles.add(tip.title);
      }
    }
  }

  // Add generic tips (lower priority)
  for (const tip of GENERIC_TIPS) {
    if (!seenTitles.has(tip.title)) {
      tips.push(tip);
      seenTitles.add(tip.title);
    }
  }

  // Sort by priority (highest first)
  return tips.sort((a, b) => b.priority - a.priority);
}

/**
 * Get the primary class name for display
 */
export function getPrimaryClassName(classNames: string[]): string {
  return classNames[0] || 'Character';
}
